import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type InvitePreview } from "../lib/api";
import { useAuth } from "../lib/auth";
import { analytics, AnalyticsEvent } from "../lib/analytics";
import { trackApiError } from "../lib/analytics/errorTracking";
import { BallMark } from "../App";
import { ChooseUsernamePage } from "./ChooseUsernamePage";
import { InlineSignIn } from "../components/InlineSignIn";
import { Button, ErrorNote, Icon, Skeleton } from "../components/ui";

/**
 * Where a share link (`/join/:code`) lands.
 *
 * Reachable at any auth state, like the claim flow — someone following an invite from WhatsApp
 * very often has no account yet, and bouncing them to a generic sign-in screen loses the reason
 * they clicked. So the three states are handled here, in place:
 *
 *   no Firebase account  →  sign in or register, without leaving the page
 *   account, no username →  the normal username step, which self-heals back into this screen
 *   ready                →  what they're joining, and a yes/no
 *
 * Nothing needs to be stashed to survive the detour: the invite code is in the URL, and none of
 * those steps navigate away from it. That's the whole trick — the intent *is* the address.
 *
 * The join itself is never automatic. Unlike a claim link (which is sent to one named person and
 * is itself the authorization), an invite code gets forwarded around group chats, and silently
 * enrolling whoever opens it would be a surprise rather than a convenience.
 */
export function JoinPage({ code }: { code: string }) {
  const { firebaseUser, user, needsProfile, isLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [joined, setJoined] = useState<{ id: string; name: string } | null>(null);

  const {
    data: preview,
    isLoading: previewLoading,
    error: previewError,
  } = useQuery({
    queryKey: ["invite", code],
    queryFn: () => api.competitions.invitePreview(code),
    // Only once there's a profile to evaluate eligibility against.
    enabled: Boolean(user),
    retry: false,
  });

  const join = useMutation({
    mutationFn: () => api.competitions.join(code),
    onSuccess: (competition) => {
      analytics.track(AnalyticsEvent.CompetitionJoined, {
        competitionId: competition.id,
        source: "link",
      });
      queryClient.invalidateQueries({ queryKey: ["competitions"] });
      setJoined({ id: competition.id, name: competition.name });
    },
    onError: (error) => trackApiError(error, "join_competition_link"),
  });

  /**
   * The username step renders its own full-screen card, so it replaces this screen rather than
   * nesting inside it. It calls `refresh()` rather than navigating, so the moment a profile
   * exists this component re-renders straight into the confirm view — same URL, same invite.
   */
  if (firebaseUser && needsProfile) return <ChooseUsernamePage />;

  return (
    <div className="relative grid min-h-dvh place-items-center overflow-hidden p-4 sm:p-6">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 12%, var(--t-accent-soft), transparent 46%)",
        }}
      />

      <div className="card animate-in relative w-full max-w-md p-6 sm:p-8">
        <div className="mb-8 flex items-center gap-2.5 text-lg font-bold text-fg">
          <BallMark size={30} />
          Last Man Standing
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-24" />
          </div>
        ) : !firebaseUser ? (
          <InlineSignIn
            eyebrow="You've been invited"
            title="Sign in to join"
            subtitle="Create an account or sign in, and you'll go straight to the competition."
          />
        ) : joined ? (
          <JoinedView name={joined.name} onContinue={() => navigate(`/c/${joined.id}`)} />
        ) : previewLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-24" />
          </div>
        ) : previewError ? (
          <InviteProblem
            error={previewError}
            onBrowse={() => navigate("/")}
          />
        ) : preview ? (
          <ConfirmJoinView
            preview={preview}
            pending={join.isPending}
            error={join.error}
            onJoin={() => join.mutate()}
            onCancel={() => navigate("/")}
            onOpen={() => navigate(`/c/${preview.id}`)}
          />
        ) : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

const BLOCKED_COPY: Record<string, { title: string; detail: string }> = {
  removed: {
    title: "You were removed from this one",
    detail: "The competition admin removed you, so this link won't let you back in.",
  },
  competition_complete: {
    title: "This competition has finished",
    detail: "It's already played out to a winner — there's nothing left to join.",
  },
  entries_closed: {
    title: "Entries are closed",
    detail: "The admin has stopped new players joining. Ask them to reopen it if you're expected.",
  },
  entry_closed: {
    title: "You've just missed it",
    detail:
      "Entry closes when round one locks, so that nobody starts with a full set of teams while everyone else has spent theirs.",
  },
};

function ConfirmJoinView({
  preview,
  pending,
  error,
  onJoin,
  onCancel,
  onOpen,
}: {
  preview: InvitePreview;
  pending: boolean;
  error: unknown;
  onJoin: () => void;
  onCancel: () => void;
  onOpen: () => void;
}) {
  if (preview.alreadyMember) {
    return (
      <div className="space-y-4 text-center">
        <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-accent-soft text-accent-text">
          <Icon name="check" className="size-6" strokeWidth={2.4} />
        </span>
        <h1 className="display-title text-2xl text-fg">You're already in</h1>
        <p className="text-sm leading-relaxed text-fg-subtle">
          {preview.name} — no need to join again.
        </p>
        <Button block onClick={onOpen}>
          Go to the competition
          <Icon name="arrowRight" className="size-4" />
        </Button>
      </div>
    );
  }

  if (!preview.canJoin) {
    const copy = BLOCKED_COPY[preview.reason ?? ""] ?? {
      title: "You can't join this one",
      detail: "Ask the person who sent you the link.",
    };
    return (
      <div className="space-y-4 text-center">
        <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-warn-soft text-warn-text">
          <Icon name="alert" className="size-6" />
        </span>
        <h1 className="display-title text-2xl text-fg">{copy.title}</h1>
        <p className="text-sm leading-relaxed text-fg-subtle">{copy.detail}</p>
        <Button variant="secondary" block onClick={onCancel}>
          Back to your competitions
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="label mb-2 text-accent-text">You've been invited</p>
        <h1 className="display-title text-3xl text-fg">Join {preview.name}?</h1>
      </div>

      <div className="surface-soft space-y-2 p-4">
        <Detail label="League" value={`${preview.league} · ${preview.season}`} />
        <Detail
          label="Players"
          value={`${preview.playersTotal} ${preview.playersTotal === 1 ? "player" : "players"} so far`}
        />
        {preview.description && <Detail label="About" value={preview.description} />}
      </div>

      <p className="text-[13px] leading-relaxed text-fg-subtle">
        Pick one team a week. Win and you're through; anything else and you're out. You can never
        pick the same team twice.
      </p>

      {/* A ternary, not `&&`: the error is `unknown`, and `unknown && <jsx/>` widens to
          `unknown` rather than narrowing to a renderable node. */}
      {error != null ? (
        <ErrorNote>
          {error instanceof ApiError ? error.message : "Couldn't join. Try again."}
        </ErrorNote>
      ) : null}

      <div className="flex gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button className="flex-1" size="lg" loading={pending} onClick={onJoin}>
          Join competition
        </Button>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
      <span className="label">{label}</span>
      <span className="min-w-0 text-right text-sm font-medium text-fg">{value}</span>
    </div>
  );
}

function JoinedView({ name, onContinue }: { name: string; onContinue: () => void }) {
  return (
    <div className="space-y-4 text-center">
      <span className="animate-pop mx-auto grid size-14 place-items-center rounded-2xl bg-accent text-accent-fg">
        <Icon name="check" className="size-7" strokeWidth={2.6} />
      </span>
      <h1 className="display-title text-3xl text-fg">You're in.</h1>
      <p className="text-sm leading-relaxed text-fg-subtle">
        {name} — make your first pick before the deadline.
      </p>
      <Button block onClick={onContinue}>
        Make your pick
        <Icon name="arrowRight" className="size-4" />
      </Button>
    </div>
  );
}

function InviteProblem({ error, onBrowse }: { error: unknown; onBrowse: () => void }) {
  const notFound = error instanceof ApiError && error.status === 404;

  return (
    <div className="space-y-4 text-center">
      <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-danger-soft text-danger-text">
        <Icon name="close" className="size-6" />
      </span>
      <h1 className="display-title text-2xl text-fg">
        {notFound ? "That invite link doesn't work" : "Couldn't load that invite"}
      </h1>
      <p className="text-sm leading-relaxed text-fg-subtle">
        {notFound
          ? "The code may have been regenerated since it was shared. Ask whoever invited you for a fresh link."
          : error instanceof ApiError
            ? error.message
            : "Check your connection and try again."}
      </p>
      <Button variant="secondary" block onClick={onBrowse}>
        Back to your competitions
      </Button>
    </div>
  );
}
